import Phaser from 'phaser';
import { GAME_FONT_FAMILY } from '@shared/constants';
import { Direction } from '@shared/types/entity';
import { getClassDisplayName } from '@shared/types/character';
import {
  PARTY_MAX_SIZE,
  type PartyView,
  type PartyFinderEntry,
  type PartyVisibility,
} from '@shared/data/party';
import { getHeadSpriteKey, getHeadFrameForDirection } from '../../sprites/HeadSpriteManager';
import { getBodySpriteKey, getBodyIdleFrame } from '../../sprites/BodySpriteManager';

export interface PartyFinderCallbacks {
  onCreate: (visibility: PartyVisibility, minLevelTag?: number) => void;
  onSetVisibility: (visibility: PartyVisibility, minLevelTag?: number) => void;
  onJoin: (partyId: string) => void;
  onRefresh: () => void;
  onClose?: () => void;
}

const C_PANEL = 0x16130b;
const C_HEADER = 0x261d0e;
const C_ACCENT = 0xc79a3e;
const C_ROW = 0x1d1810;
const C_ROW_SEL = 0x3a2e14;
const C_BTN = 0x2a2210;
const T_BODY = '#e8dcb0';
const T_DIM = '#9a8a5a';

const WIDTH = 560;
const HEIGHT = 400;
const HEADER_H = 34;
const PAD = 12;
const LIST_W = 260;
const AVATAR = 34;

function font<T extends Phaser.Types.GameObjects.Text.TextStyle>(style?: T): T {
  return { fontFamily: GAME_FONT_FAMILY, resolution: 2, ...style } as T;
}

/**
 * PartyFinderPanel — buscador de grupos que abre el NPC "Heraldo de Grupos":
 * crear party propio (público/cerrado + tag de nivel), lista de parties públicas
 * a la izquierda, detalle con los integrantes a la derecha, botón Unirse.
 * Ver docs/PARTY_SYSTEM.md.
 */
export class PartyFinderPanel {
  private readonly scene: Phaser.Scene;
  private readonly cb: PartyFinderCallbacks;
  private root: Phaser.GameObjects.Container;
  private body: Phaser.GameObjects.Container;
  private x: number;
  private y: number;
  private visible = false;

  private party: PartyView | null = null; // party propio (o null)
  private results: PartyFinderEntry[] = [];
  private selectedId: string | null = null;
  private draftVisibility: PartyVisibility = 'public';
  private draftLevel = 1;

  constructor(scene: Phaser.Scene, cb: PartyFinderCallbacks, initialX: number, initialY: number) {
    this.scene = scene;
    this.cb = cb;
    this.x = initialX;
    this.y = initialY;
    this.root = scene.add.container(this.x, this.y).setDepth(1041).setScrollFactor(0);
    this.body = scene.add.container(0, HEADER_H);
    this.root.add(this.body);
    this.buildHeader();
    this.root.setVisible(false);
  }

  isVisible(): boolean {
    return this.visible;
  }

  open(): void {
    this.visible = true;
    this.root.setVisible(true);
    this.rebuild();
  }

  hide(): void {
    this.visible = false;
    this.root.setVisible(false);
  }

  setParty(party: PartyView | null): void {
    this.party = party;
    if (this.visible) this.rebuild();
  }

  setResults(results: PartyFinderEntry[]): void {
    this.results = results;
    if (this.selectedId && !results.some((r) => r.id === this.selectedId)) this.selectedId = null;
    if (this.visible) this.rebuild();
  }

  private buildHeader(): void {
    const bg = this.scene.add.rectangle(0, 0, WIDTH, HEADER_H, C_HEADER).setOrigin(0, 0);
    bg.setStrokeStyle(1, C_ACCENT, 0.6);
    bg.setInteractive({ useHandCursor: true });
    this.enableDrag(bg);
    const title = this.scene.add
      .text(PAD, HEADER_H / 2, 'BUSCADOR DE GRUPOS', font({ fontSize: '15px', color: T_BODY }))
      .setOrigin(0, 0.5);
    const refresh = this.scene.add
      .text(WIDTH - PAD - 22, HEADER_H / 2, '⟳', font({ fontSize: '16px', color: T_DIM }))
      .setOrigin(1, 0.5)
      .setInteractive({ useHandCursor: true });
    refresh.on('pointerdown', () => this.cb.onRefresh());
    const close = this.scene.add
      .text(WIDTH - PAD, HEADER_H / 2, '✕', font({ fontSize: '16px', color: T_DIM }))
      .setOrigin(1, 0.5)
      .setInteractive({ useHandCursor: true });
    close.on('pointerdown', () => {
      this.hide();
      this.cb.onClose?.();
    });
    this.root.add([bg, title, refresh, close]);
  }

  private enableDrag(handle: Phaser.GameObjects.Rectangle): void {
    let dragging = false;
    let ox = 0;
    let oy = 0;
    handle.on('pointerdown', (p: Phaser.Input.Pointer) => {
      dragging = true;
      ox = p.x - this.x;
      oy = p.y - this.y;
    });
    this.scene.input.on('pointermove', (p: Phaser.Input.Pointer) => {
      if (!dragging) return;
      this.x = p.x - ox;
      this.y = p.y - oy;
      this.root.setPosition(this.x, this.y);
    });
    this.scene.input.on('pointerup', () => {
      dragging = false;
    });
  }

  private rebuild(): void {
    this.body.removeAll(true);
    const bg = this.scene.add.rectangle(0, -HEADER_H, WIDTH, HEADER_H + HEIGHT, C_PANEL).setOrigin(0, 0);
    bg.setStrokeStyle(1, C_ACCENT, 0.5);
    this.body.add(bg);
    this.body.sendToBack(bg);

    this.buildTopBar();
    this.buildList();
    this.buildDetail();
  }

  /** Barra superior: crear party o gestionar visibilidad del propio. */
  private buildTopBar(): void {
    const y = 8;
    if (!this.party) {
      const label = this.scene.add
        .text(PAD, y + 6, 'Crear grupo:', font({ fontSize: '12px', color: T_DIM }))
        .setOrigin(0, 0);
      this.body.add(label);
      this.addToggle(110, y, `Estado: ${this.draftVisibility === 'public' ? 'Público' : 'Cerrado'}`, () => {
        this.draftVisibility = this.draftVisibility === 'public' ? 'closed' : 'public';
        this.rebuild();
      });
      this.addToggle(250, y, `Nivel: ${this.draftLevel}`, () => {
        this.draftLevel = this.draftLevel >= 50 ? 1 : this.draftLevel + 1;
        this.rebuild();
      });
      this.addButton(WIDTH - PAD - 90, y, 90, 'Crear', () =>
        this.cb.onCreate(this.draftVisibility, this.draftVisibility === 'public' ? this.draftLevel : undefined)
      );
    } else {
      const vis = this.party.visibility === 'public' ? 'Público' : 'Cerrado';
      const label = this.scene.add
        .text(PAD, y + 6, `Tu grupo (${this.party.members.length}/${PARTY_MAX_SIZE}) — ${vis}`, font({ fontSize: '12px', color: T_BODY }))
        .setOrigin(0, 0);
      this.body.add(label);
      this.addToggle(WIDTH - PAD - 150, y, this.party.visibility === 'public' ? 'Hacer cerrado' : 'Hacer público', () => {
        const next: PartyVisibility = this.party?.visibility === 'public' ? 'closed' : 'public';
        this.cb.onSetVisibility(next, next === 'public' ? this.party?.minLevelTag ?? 1 : undefined);
      });
    }
  }

  private buildList(): void {
    const top = 44;
    const header = this.scene.add
      .text(PAD, top, 'Grupos públicos', font({ fontSize: '12px', color: T_DIM }))
      .setOrigin(0, 0);
    this.body.add(header);

    if (this.results.length === 0) {
      const empty = this.scene.add
        .text(PAD, top + 24, 'No hay grupos públicos ahora.', font({ fontSize: '12px', color: T_DIM }))
        .setOrigin(0, 0);
      this.body.add(empty);
      return;
    }

    let ry = top + 22;
    for (const entry of this.results) {
      const sel = this.selectedId === entry.id;
      const row = this.scene.add.rectangle(PAD, ry, LIST_W - PAD, 40, sel ? C_ROW_SEL : C_ROW).setOrigin(0, 0);
      row.setInteractive({ useHandCursor: true });
      row.on('pointerdown', () => {
        this.selectedId = entry.id;
        this.rebuild();
      });
      const name = this.scene.add
        .text(PAD + 8, ry + 6, entry.leaderName, font({ fontSize: '13px', color: T_BODY }))
        .setOrigin(0, 0);
      const meta = this.scene.add
        .text(
          PAD + 8,
          ry + 22,
          `${entry.memberCount}/${entry.maxSize}${entry.minLevelTag ? ` · Nivel ${entry.minLevelTag}` : ''}`,
          font({ fontSize: '11px', color: T_DIM })
        )
        .setOrigin(0, 0);
      this.body.add([row, name, meta]);
      ry += 44;
    }
  }

  private buildDetail(): void {
    const x0 = LIST_W + 8;
    const top = 44;
    const sep = this.scene.add.rectangle(LIST_W, HEADER_H, 1, HEIGHT - HEADER_H, C_ACCENT, 0.3).setOrigin(0, 0);
    // sep is positioned in body coords; body is offset by HEADER_H so use y=0.
    sep.setPosition(LIST_W, 0);
    this.body.add(sep);

    const entry = this.results.find((r) => r.id === this.selectedId);
    if (!entry) {
      const hint = this.scene.add
        .text(x0, top, 'Elegí un grupo para ver sus integrantes.', font({ fontSize: '12px', color: T_DIM }))
        .setOrigin(0, 0)
        .setWordWrapWidth(WIDTH - x0 - PAD);
      this.body.add(hint);
      return;
    }

    const title = this.scene.add
      .text(x0, top, `Grupo de ${entry.leaderName}`, font({ fontSize: '14px', color: T_BODY }))
      .setOrigin(0, 0);
    this.body.add(title);

    let ry = top + 26;
    for (const m of entry.members) {
      const face = this.createFace(m.headId, m.bodyId);
      const bg = this.scene.add.rectangle(x0, ry, AVATAR + 4, AVATAR + 4, 0x000000, 0.35).setOrigin(0, 0);
      this.body.add(bg);
      if (face) {
        face.setPosition(x0 + (AVATAR + 4) / 2, ry + (AVATAR + 4) / 2);
        this.body.add(face);
      }
      const nm = this.scene.add
        .text(x0 + AVATAR + 12, ry + 2, `${m.isLeader ? '★ ' : ''}${m.name}`, font({ fontSize: '13px', color: T_BODY }))
        .setOrigin(0, 0);
      const sub = this.scene.add
        .text(x0 + AVATAR + 12, ry + 18, `Nv ${m.level} · ${getClassDisplayName(m.classId)}`, font({ fontSize: '11px', color: T_DIM }))
        .setOrigin(0, 0);
      this.body.add([nm, sub]);
      ry += AVATAR + 10;
    }

    // Botón Unirse (solo si no estoy en party y hay lugar).
    const canJoin = !this.party && entry.memberCount < entry.maxSize;
    if (canJoin) {
      this.addButton(x0, HEIGHT - 40, WIDTH - x0 - PAD, 'Unirse al grupo', () => this.cb.onJoin(entry.id));
    } else if (this.party) {
      const note = this.scene.add
        .text(x0, HEIGHT - 36, 'Ya estás en un grupo.', font({ fontSize: '11px', color: T_DIM }))
        .setOrigin(0, 0);
      this.body.add(note);
    }
  }

  private addToggle(x: number, y: number, label: string, onClick: () => void): void {
    this.addButton(x, y, Math.max(90, label.length * 7 + 20), label, onClick);
  }

  private addButton(x: number, y: number, w: number, label: string, onClick: () => void): void {
    const bg = this.scene.add.rectangle(x, y, w, 26, C_BTN).setOrigin(0, 0);
    bg.setStrokeStyle(1, C_ACCENT, 0.5);
    bg.setInteractive({ useHandCursor: true });
    const t = this.scene.add
      .text(x + w / 2, y + 13, label, font({ fontSize: '12px', color: T_BODY }))
      .setOrigin(0.5, 0.5);
    bg.on('pointerdown', onClick);
    this.body.add([bg, t]);
  }

  private createFace(headId: number, bodyId: number): Phaser.GameObjects.Sprite | null {
    let key: string | null = null;
    let frame: number | string = 0;
    if (headId > 0 && this.scene.textures.exists(getHeadSpriteKey(headId))) {
      key = getHeadSpriteKey(headId);
      frame = getHeadFrameForDirection(Direction.DOWN);
    } else if (bodyId > 0 && this.scene.textures.exists(getBodySpriteKey(bodyId))) {
      key = getBodySpriteKey(bodyId);
      frame = getBodyIdleFrame(Direction.DOWN, bodyId);
    }
    if (!key) return null;
    const spr = this.scene.add.sprite(0, 0, key, frame);
    const scale = AVATAR / Math.max(1, Math.max(spr.width, spr.height));
    spr.setScale(scale);
    return spr;
  }

  destroy(): void {
    this.root.destroy(true);
  }
}
